import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { UnitGroup, UnitOption } from '@/services/mobi-api/unit/types'
import { resolveImageUrl } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { Radio, TextField } from '@mui/material'
import classNames from 'classnames'
import Image from 'next/image'
import React, { FunctionComponent, useEffect } from 'react'
import { isNil } from '../../../../../utils/validations'

interface Props {
  handleNext: (payload: CreateInvoiceReq, option: UnitOption) => void
}

const UnitStep1: FunctionComponent<Props> = ({ handleNext }) => {
  const { showError } = useToast()
  const { translate } = useIntl()
  const { locale } = useLanguage()
  const { unit } = useMobiApi()
  const [loading, setLoading] = React.useState(false)
  const [numberInput, setNumberInput] = React.useState<string | undefined>()
  const [clickedCard, setClickedCard] = React.useState<number | undefined>()

  const [clickedOption, setClickedOption] = React.useState<
  number | undefined
  >()
  const [unitList, setUnitList] = React.useState<UnitGroup[] | undefined>()

  const handleNumber = (phoneNum: string): void => {
    if (phoneNum.length <= 8) {
      setNumberInput(phoneNum)
    }
  }
  useEffect(() => {
    if (numberInput?.length === 8) {
      void fetchUnit()
    }
  }, [numberInput])
  const fetchUnit = async (): Promise<void> => {
    try {
      setLoading(true)
      const res = await unit.getList({ isdn: numberInput })
      if (res !== undefined) {
        if (res.list.length > 0) {
          setUnitList(res.list)
          setClickedCard(0)
        } else {
          showError(
            locale === 'en'
              ? res.cause?.messageEn ?? translate('error')
              : res.cause?.messageMn ?? translate('error')
          )
        }
      } else {
        showError(translate('error.isdnError'))
        setUnitList([])
      }
    } catch (e) {
      setUnitList([])
      showError(translate('error'))
    } finally {
      setLoading(false)
    }
  }
  const clickNext = (): void => {
    if (
      unitList !== undefined &&
      clickedCard !== undefined &&
      clickedOption !== undefined
    ) {
      const option = unitList[clickedCard]?.options[clickedOption]
      const payload: CreateInvoiceReq = {
        isdn: numberInput ?? '',
        products: [
          {
            productCode: option.productCode,
            productType: option.productType
          }
        ]
      }
      handleNext(payload, option)
    }
  }
  return (
    <div className='bg-surfaceInverse rounded-b-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
      <div className='sticky top-0 z-50 p-4 md:p-6 bg-surfaceInverse'>
        <TextField
          value={numberInput}
          id='outlined-basic'
          label={translate('phoneNumber')}
          variant='outlined'
          type='number'
          onChange={(e) => {
            handleNumber(e.target.value)
          }}
          className='w-full md:w-96 rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh'
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
      </div>
      <div className='flex flex-col pb-24 flex-grow p-4 overflow-auto md:p-8 md:h-full'>
        {loading && (
          <div className='flex items-center justify-center w-full h-96'>
            <Loader />
          </div>
        )}
        {unitList !== undefined && unitList.length > 0
          ? (
            <div>
              <div className='md:py-2'>
                <div className='flex flex-col gap-2 mt-2 md:mt-0'>
                  <span className='text-sm font-bold text-textMid md:text-base'>
                    {translate('digitalCard')}
                  </span>
                </div>
                <div className='grid grid-cols-3 gap-2 py-4 md:w-96'>
                  {unitList.map((unit, index) => (
                    <div
                      key={index}
                      className={classNames(
                        'group/item rounded-2xl bg-surfaceWebGrey flex flex-col items-center cursor-pointer border hover:border-[#EF4444]',
                        clickedCard === index
                          ? 'border-[#EF4444]'
                          : 'border-surfaceWebGrey'
                      )}
                      onClick={() => setClickedCard(index)}
                    >
                      <div className='relative w-full aspect-[10/6.5] rounded-2xl overflow-hidden p-0'>
                        <Image
                          draggable={false}
                          fill
                          alt='img'
                          src={unit.image ?? ''}
                          loading='lazy'
                          objectFit='cover'
                        />
                      </div>
                      <div className='px-3 -translate-y-2 border rounded-lg bg-surfaceInverse border-secondaryMidStroke'>
                        <span
                          className={classNames(
                            'group/edit text-sm font-bold group-hover/item:text-[#EF4444]',
                            clickedCard === index
                              ? 'text-[#EF4444]'
                              : 'text-textMid'
                          )}
                        >
                          {unit.price.toLocaleString()}₮
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {clickedCard !== undefined
                  ? (
                    <div className='w-full md:w-96 py-4 border-t border-dashed'>
                      <span className='text-sm font-bold text-textMid'>
                        {translate('cardOptions')}
                      </span>
                      <div className='flex flex-col gap-3 mt-4'>
                        {unitList[clickedCard].options.map((options, index) => (
                          <div
                            key={index}
                            className={classNames(
                              'p-2 flex justify-between items-center border bg-surfaceWebGrey rounded-xl cursor-pointer hover:border-[#EF4444]',
                              clickedOption === index
                                ? 'border-[#EF4444]'
                                : 'border-secondaryMidStroke'
                            )}
                            onClick={() => setClickedOption(index)}
                          >
                            <div className='flex items-center justify-center w-2/3 gap-3'>
                              <Radio
                                color='error'
                                checked={clickedOption === index}
                                className='text-textHigh'
                              />
                              <span className='text-xs font-bold text-textHigh md:text-sm w-52'>
                                {locale === 'en'
                                  ? options.descriptionEn
                                  : options.descriptionMn}
                              </span>
                            </div>
                            {!isNil(options.days) && (
                              <div
                                className={classNames(
                                  'px-2 py-1 rounded-lg flex flex-col justify-center items-center border',
                                  clickedOption === index
                                    ? 'bg-[#FDE7E7] border-[#FDE7E7]'
                                    : 'bg-surfaceInverse border border-secondaryMidStroke'
                                )}
                              >
                                <span className='text-[#EF4444] font-bold'>
                                  {options.days}
                                </span>
                                <span
                                  className={classNames(
                                    'text-[11px] font-semibold',
                                    clickedOption === index
                                      ? 'text-black'
                                      : 'text-textHigh'
                                  )}
                                >
                                  {translate('day')}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    )
                  : (
                    <></>
                    )}
              </div>
            </div>
            )
          : (
            <div className='flex items-start gap-3 border border-[#F4A610] bg-[#FEFCF5] rounded-lg p-3 md:w-96'>
              <Image
                draggable={false}
                width={30}
                height={30}
                src={resolveImageUrl('/uploads/icons/warning.svg')}
                alt='!'
              />
              <span className='text-xs text-[#78350F] font-medium ml-2'>
                {translate('unit.warning')}
              </span>
            </div>
            )}
      </div>
      <div className='fixed bottom-0 left-0 right-0 z-50 p-4 border-t md:sticky md:p-6 bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton
          fullwidth
          disabled={clickedCard === undefined || clickedOption === undefined}
          onClick={clickNext}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}
export default UnitStep1
